import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/54350176/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const Caption = makeShortcode("Caption");
const Title = makeShortcode("Title");
const DoDontRow = makeShortcode("DoDontRow");
const DoDont = makeShortcode("DoDont");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`Notifications are an important method of communicating with users and providing feedback. They range from granular, inline responses to user actions to system-wide messages.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Overview</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Designing with notifications</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Notification status</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Notification types</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Considerations when designing</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Accessibility</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Related components and patterns</AnchorLink>
      <AnchorLink mdxType="AnchorLink">References</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Feedback</AnchorLink>
    </AnchorLinks>
    <h2>{`Overview`}</h2>
    <p>{`It’s important to keep users informed and send notifications when there is an update or status change they should be aware of. Users should always be given appropriate and timely messages to help them understand whether they are moving towards their goal. Ensure your notifications are relevant, timely, and informative.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key principle`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Definition`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Relevant`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Notifications should be related to the user’s goals and presented in the context of what they are doing`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Timely`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Ensure users are kept up to date with prompt notifications and see critical notifications immediately`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Informative`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provide users with the context and next steps needed to understand and address the notification`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Be considerate of your users when sending a notification. Notifications that are too frequent or disruptive create negative experiences and drive users from platforms.`}</p>
    <p>{`Notifications are comprised of status and type. Their status signifies the purpose of the information being conveyed. Notification types allow you to tailor the disruptiveness of the notification to the specific situation. Notification status and type options in Carbon should be combined to create notifications that are relevant, timely, and informative for each use case.`}</p>
    <Row mdxType="Row">
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4>{`Notification status`}</h4>
        <ul>
          <li parentName="ul">{`Informational`}</li>
          <li parentName="ul">{`Success`}</li>
          <li parentName="ul">{`Warning`}</li>
          <li parentName="ul">{`Error`}</li>
        </ul>
      </Column>
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4>{`Notification type`}</h4>
        <ul>
          <li parentName="ul">{`Inline`}</li>
          <li parentName="ul">{`Toast`}</li>
          <li parentName="ul">{`Banner`}</li>
          <li parentName="ul">{`Notification panel`}</li>
          <li parentName="ul">{`Modal`}</li>
        </ul>
      </Column>
    </Row>
    <h2>{`Designing with notifications`}</h2>
    <h3>{`When to use`}</h3>
    <p>{`Use notifications to inform users of important status changes and updates. Transparency is a core element of building user trust and is the first of Jakob Nielsen’s `}<a parentName="p" {...{
        "href": "https://www.nngroup.com/articles/ten-usability-heuristics/"
      }}>{`10 usability heuristics`}</a>{`. They should be relevant to the user and as minimally disruptive as possible. There are two major use cases for notifications: task-generated and system-generated.`}</p>
    <h4>{`Task-generated notifications`}</h4>
    <p>{`Task-generated notifications are initiated in response to user action during a specific task. They give users direct, immediate feedback. They should be placed in the region of the page the user is working in and be related to the user’s action.`}</p>
    <p>{`You might send a task-generated notification when:`}</p>
    <ul>
      <li parentName="ul">{`A form is successfully submitted`}</li>
      <li parentName="ul">{`There is a problem uploading a file`}</li>
      <li parentName="ul">{`Credentials can’t be found`}</li>
    </ul>
    <h4>{`System-generated notifications`}</h4>
    <p>{`These notifications are initiated by the application or system, independent of user action. They provide updates on background system status or out-of-context events that have finished.`}</p>
    <p>{`You might send a system-generated notification when:`}</p>
    <ul>
      <li parentName="ul">{`A user loses network connection`}</li>
      <li parentName="ul">{`Planned system maintenance is coming soon`}</li>
      <li parentName="ul">{`A new report is ready`}</li>
      <li parentName="ul">{`The user’s login session is about to expire`}</li>
    </ul>
    <h3>{`When not to use`}</h3>
    <p>{`Only send notifications where necessary. Confine each notification to the portion of the interface and workflow it is relevant to. Being interrupted creates a frustrating and discouraging experience for users, so this should be limited as much as possible. Additionally, frequent distractions `}<a parentName="p" {...{
        "href": "https://link.springer.com/chapter/10.1007/978-1-4842-4221-6_9#Sec22"
      }}>{`lower productivity`}</a>{` and can lead to `}<a parentName="p" {...{
        "href": "https://psnet.ahrq.gov/primer/alert-fatigue"
      }}>{`alert fatigue`}</a>{`.`}</p>
    <h2>{`Notification status`}</h2>
    <p>{`Notification status helps convey the information being communicated. These statuses correspond with a color and icon to provide a consistent, universal experience for users.`}</p>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "45.80357142857143%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAAA+ElEQVQoz62S2W6DMBBF+f9fbKVmIUpwAhjD2Ga83I5dmlaq2uYhyEfY6HDNDG6AjP+unHPlEa8pnl8jrGeQY7kHOFm7NcBzQoi5mFWm4GB4wbwKTFgqFku0WBPX0BqotMdJTWg7jdPV4DIQLj3hZmQTn1CrkHGgM16mPV4HYdzjzbSVg7tgDOYj8Jklb19Y5ISUflKef4bVdfrNy3eaR5v9l5e//5QySe4I0jvo2w5G+kNTW2F7RpLe5E2OnYJVCrrrMKsrqO9hhTTPkuG2koscRkQ/gN2A4GW+jghCYo0c3T2wvBiXBSwEokq0FlnCMvNXD595Dt8BEn3Dh5eoROsAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of notification status",
            "title": "Example of notification status",
            "src": "/static/389fe76ec86370a5cd0926eb230196fc/3cbba/1_notification_status_1120.png",
            "srcSet": ["/static/389fe76ec86370a5cd0926eb230196fc/7fc1e/1_notification_status_1120.png 288w", "/static/389fe76ec86370a5cd0926eb230196fc/a5df1/1_notification_status_1120.png 576w", "/static/389fe76ec86370a5cd0926eb230196fc/3cbba/1_notification_status_1120.png 1152w", "/static/389fe76ec86370a5cd0926eb230196fc/0b124/1_notification_status_1120.png 1728w", "/static/389fe76ec86370a5cd0926eb230196fc/99571/1_notification_status_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Notification status using the inline notification component</Caption>
      </Column>
    </Row>
    <br />
    <Title mdxType="Title"> Deciding what to use </Title>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Status`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Action`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Color`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Icon`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Informational`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provide additional information to users that may not be tied to their current action or task`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Do not require immediate action and can be dismissed on a timer or persist, depending on the content`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Blue`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No icon`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Success`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Confirm a task was completed as expected`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Typically do not require further action and can be dismissed automatically or persist in a nonintrusive manner`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Green`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`checkmark filled`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Warning`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Inform users that they are taking actions that are not desirable or might have unexpected results`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Often persist until the user dismisses the notification or continues in their task`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Yellow`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`warning filled`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Error`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Inform users of an error or critical failure and optionally block the user from proceeding until the issue has been resolved`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Always persist until the user dismisses them or resolves error`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Red`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`error filled`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h2>{`Notification types`}</h2>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "45.80357142857143%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAABhElEQVQoz31S30vCYBTdH2O+VP9Z1EtCUI89BdWD5ENg0kNBL4sCY4MtaeJE8CFCSRzNFPXB35sbOXWedi85QqwDZ7vb/e757rnfJywWCwyHQ/R6PfT7fXS7XTiOA8JsNoPruiGDpaD1BMdxf2oGwbuP0WjEOWE+n0PTslBVFVlNg6IoqFarXKQF35FIBNHoBja3tnF8EsdH7ZNzr28liA9pPMkKHtMSXrI5+L4PgVTnPkJQV7QJodPpQJIkyLLMVNVndkGg4lVwh/Sw3Qks5wt2wPF4jMlkElpbB8qRIG38mwThr8LlgnUgF9PpNKTneWHMMywUCshkMsjlchDFexSLRS4cDAbQdZ2ZJ+Z1WJbFArZts5tVcod0Wu12O+jKx9HhAfZ2d1iwVCohmUyiXC7jvWIglbqGYVTZMgnSbVhlaHk55POzU8Ri+xwbhoFE4gI3t3e4EiuIX96hUTeDQ/T/FyQxmguh1WqhXq9zTIfTbDZhmibMWiOI28GcPP5P9tYJfgMXrZgp9UshswAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Examples of basic notification types",
            "title": "Examples of basic notification types",
            "src": "/static/92d3722369c9eefb4b33355eea345b37/3cbba/2_Notification_types_1120.png",
            "srcSet": ["/static/92d3722369c9eefb4b33355eea345b37/7fc1e/2_Notification_types_1120.png 288w", "/static/92d3722369c9eefb4b33355eea345b37/a5df1/2_Notification_types_1120.png 576w", "/static/92d3722369c9eefb4b33355eea345b37/3cbba/2_Notification_types_1120.png 1152w", "/static/92d3722369c9eefb4b33355eea345b37/0b124/2_Notification_types_1120.png 1728w", "/static/92d3722369c9eefb4b33355eea345b37/99571/2_Notification_types_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <br />
    <Title mdxType="Title"> Deciding what to use </Title>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Duration and interaction`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Inline`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provide users with nondisruptive feedback or the status of an action`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Persist until the message is resolved or dismissed by user and may include a ghost button action`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Toast`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Short, time-based messages that slide in and out of a page and provide nondisruptive information`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Disappear automatically or can be dismissed by user`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Banner`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`System or product level notifications that are not specific to a task`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Persist until dismissed by user and may include a ghost button or link`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Notification panel`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Notification center that provides users with system-generated messages`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opened and closed by user`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Modal`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Highly disruptive notifications that provide users with critical information that needs their attention or action`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Persist and block tasks until dismissed by user. Modals allow more user action than other notifications and can include other Carbon components.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Inline notification`}</h3>
    <p><a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/components/notification/usage/"
      }}>{`Inline notifications`}</a>{` are nondisruptive and confined to a specific area in the UI. Inline notifications display both task-generated and system-generated messages and persist until they are dismissed by the user or the notification is resolved. They are frequently used in conjunction with field-level messages for errors in forms or other input areas.`}</p>
    <p>{`Carbon offers low-contrast and high-contrast inline notifications. All inline notifications use a color that corresponds with their message intent and can also be accompanied by an icon to reinforce the message intent.`}</p>
    <h4>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Place inline notifications near their related items.`}</li>
      <li parentName="ul">{`In forms, inline notifications can be placed either at the top or bottom.`}</li>
      <li parentName="ul">{`Keep the message under two lines.`}</li>
      <li parentName="ul">{`Do not cover other content with inline notifications.`}</li>
      <li parentName="ul">{`The notification width varies based on the context and page layout.`}</li>
      <li parentName="ul">{`Be descriptive and give users clear next steps.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.53804347826086%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACLklEQVQ4y62Ty44SQRSG+w2YsGMw4X4LIaMLRKIiITHBxOdwBwvuRHbIblwQFq647EwwosS3IZoIIxcHQjcNDd2A/NYpGGbGGHVhJX+qurrqq/+cqiPgPzdhu92iUqkgk8kgn88jm80il8sde5q/qXQ6jUKhgFKpdFSxWES5XIYsyxA2mw3Oz18jnckikUgglUrxTclkkisejx8Vi8X4XDQaxcnJCfR6PZdOp4PX68VgMIBwZXWlrrFZr/l4t9v9Max2uw232w2fzwePxwOHw4FIJILRaASBts7nMiaTMURRxGw2gyRJbG6OxWJxS/SPWqPRgNPphN/v52Cr1YpQKIThcLh3KIoSer0exuMxlsslFEXh+h2Q3BOQnJFDcme32xEOh/dAWkCLyZWmaVBV9ajVanXrm9ZRq9frMJlMcLlcHGixWBAMBg8hMyC5oRu6CSCnlALqfwVWq1UYDAYOpNDNZjMCgcAeyEOeTnm4BF4ewr06hCAEXR6+qdVqNRiNxj3Q5YHJbMP9QPAa+LXbRffiAgvKG9OcQebK4np8kHS4FArZbrPh7Owu3DYj7nnu4FkkwHLY39+yMvqO2ecvmLKkiv0+ZHbSjIl6lbmXxxJmlxKUyQQ/WCE0m008fPQY0adhnD55CeHBG5w+r+Lb5XwP1BYKNHb6ij0VksrcqId+zVyulSWXxsZUCJ1OB63WB3z6+A6v3ot48RbItljqFFZ6+Msj3v1TBW+Ytnz1T32ITZ1gkrC7AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Inline notification in a product",
            "title": "Inline notification in a product",
            "src": "/static/56e60ccbb93529985307bb4d32fc5348/3cbba/3_Inline_736.png",
            "srcSet": ["/static/56e60ccbb93529985307bb4d32fc5348/7fc1e/3_Inline_736.png 288w", "/static/56e60ccbb93529985307bb4d32fc5348/a5df1/3_Inline_736.png 576w", "/static/56e60ccbb93529985307bb4d32fc5348/3cbba/3_Inline_736.png 1152w", "/static/56e60ccbb93529985307bb4d32fc5348/392b1/3_Inline_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Low-contrast inline notification</Caption>
      </Column>
    </Row>
    <h3>{`Toast`}</h3>
    <p><a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/components/notification/usage/"
      }}>{`Toasts`}</a>{` are notifications that slide in and out, typically in the top right of the page. They are more disruptive than inline notifications and are best used with system-generated messages that do not correspond to a specific section of the UI. Toasts disappear automatically after a set amount of time but can also be dismissed by the user.`}</p>
    <p>{`Carbon toasts can be either low-contrast or high-contrast. Their color should correspond with the message intent and they can also use an icon to convey the message intent.`}</p>
    <h4>{`Long messages`}</h4>
    <p>{`Removing the timestamp provides space for a third line of content for longer toast messages. Toasts are intended to be at-a-glance messages confined to a small region of the screen so their messages should not exceed three lines.`}</p>
    <p>{`If it is possible a toast will have a message longer than three lines, include a short message with a “View more” link that takes the user to a view of the full notification message. This can be either a full page with more details or a modal.`}</p>
    <h4>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Multiple toasts stack horizontally, with the newest appearing at the top of the list.`}</li>
      <li parentName="ul">{`Keep messages clear and concise`}</li>
      <li parentName="ul">{`Toasts dismiss after five seconds.`}</li>
      <li parentName="ul">{`The timestamp is optional and can be removed.`}</li>
      <li parentName="ul">{`Toast notifications have a fixed width and should not be expanded to fit the content area.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.53804347826086%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACWElEQVQ4y62Ty09TURCHqyIUJLSVKK9WFxAQIUokPgBFTSBx6dKtrv0PTEwAF/RFS41sTaiJC4Mr/wETowuiwfggLmqq0FKglftqe/uAz3NPUwI+VnqTL3POb05m5syZa+M/f7ZyuUw0GsXn8xEIBPD7/fuwdK/Xu4vcT3t5MDnFlMXEBBP37zEb9KFrKrZiscjQ0BCNjY04nU4cDoe0Fi6XS+p7OdLQgPOok97Bfs6cP8vA8DB9124xcvMuK8kMtkKhwPj4OF1dXZK2tjba29ulbW1tlbZKR0cHza5mBi9fYFH5xNL6Mp/1byRQeLmaJpY0KwHHxsbo7Oyku7sbt9uNx+OhpaXlN6wELoeTgYvneL3yjlexRd58X+KDEuPFxy/EEkblyldHR8VVm3Yrs+ze9a+a2+Omp+8UPf399J32cPLSbXrvvCexKSo0TZORK9dxHT8henOM+vp67Ha7tA2iX3V1ddJWNYva2lpqDh2m5qCNA0292HoeY7/xlpWNQqXChYXnzEefMDc3RygUkq8dCoeZjUQIBINEIg+ZmQlJLRyexS/8weAMwcA0/kfPmJxfJfR0GdUoYZPDs7Pzj9NXEhRFmO1KwEwmQzweZy2VQtM0FEXl65pO8kee5FaWpGKQUHTWhS+R1ohv5EgpOeEzSKpZElqWtG6wvS0C7ojqdF1HVVUZzFobhkFqU8HI5rBq30WcVfQsqfQWpVJ5n88U02L9JDKg9TBV8vm8tLlcFk0ksfaSXA6r34WCKRKrMqlpVvWCSFCqVPi3rlS7aiXcy74zf9B/AhA4/EOjhDB3AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Toast notification in a product",
            "title": "Toast notification in a product",
            "src": "/static/c734aa8e9a5a84c0f082fff2628fc48d/3cbba/4_Toast_736.png",
            "srcSet": ["/static/c734aa8e9a5a84c0f082fff2628fc48d/7fc1e/4_Toast_736.png 288w", "/static/c734aa8e9a5a84c0f082fff2628fc48d/a5df1/4_Toast_736.png 576w", "/static/c734aa8e9a5a84c0f082fff2628fc48d/3cbba/4_Toast_736.png 1152w", "/static/c734aa8e9a5a84c0f082fff2628fc48d/392b1/4_Toast_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Low-contrast toast noticiation</Caption>
      </Column>
    </Row>
    <h3>{`Banner`}</h3>
    <p>{`Banners take over the top of an interface to show general notifications for the product or system, not a specific task. They persist until they are dismissed by the user. Banners can include a ghost button or link. Depending on the message, the user resolving a product or system issue (for example, updating necessary account information), may also dismiss the banner. Banners may also persist across multiple sessions.`}</p>
    <h4>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Banners should be placed at the top of the content area they relate to.`}</li>
      <li parentName="ul">{`Do not cover other content with a banner notification.`}</li>
      <li parentName="ul">{`Place system-wide messages directly below the main header or navigation bar.`}</li>
      <li parentName="ul">{`Banners are not sticky and should scroll with the other content on the page.`}</li>
      <li parentName="ul">{`Only show one banner at a time.`}</li>
    </ul>
    <p>{`More design iteration and user testing is needed before Carbon solidifies our guidance for banners and creates a banner component. Keep an eye out for refined usage examples in the future. If your team has developed a banner component, consider contributing it to Carbon so that it can be made available to others.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.53804347826086%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAAB3klEQVQ4y62STY/aMBCG+a8LqGR7pULAEYlzL/Ta39BTj/0BPbSHSpV2L1tBvsD5Tuw4tt+OHZJFaKVeaunVjGecZ8bjTPCf10QphcPhgPV6je12i81mc6Oti/9Lq9UK+/0eeZ5bYIfdbofFwiO9g0d69BZO7x89zOczzGdTzO40xqZTTB8esFwukSQJJtoYFBzISSXXaDUgFOnGdnQV9YbM3XUNsRwwiDO8HEOyKcJLjojlzgbnDCGJZTXSUjhlVYukEMgrgSBp8e2XxNcfEt+fqCOjMLHkKArx/PyEcxwhJj8Kg94nhYHv9jbu+yecTkeEYQgW+/j5O8CHzwzepws+fkmJpHsgY4wAMcqyBEsuOPp/aMAFsizDyfeRpimKoqQCMRWPyC9wPl9cIclzKJH3VhPQGA3RKjSS+G4otJf1OBdNh95adl5Syus3NGelYf+YiU3UXIKV9CCdoUSLrGTQdMDCqqpC27YO0HWdiw0wm+sIMuyvQI1GdCgaCtLTKS1R1hkMlbYHhRDOWg2wAc45H29g9yOw5j2wlbb1HqgJqClnP1LXLgY7AJumcbE7IHVBpIagnDq1rXPRkO3GLmyX1rc5awffxoeYm6d7lNcx9zKvQ7e6fYRb3ceH9Rdin7xh5gTVNwAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Banner notification in a product",
            "title": "Banner notification in a product",
            "src": "/static/23a7237a553ebc88cda48d1db2ab9714/3cbba/5_Banner_736.png",
            "srcSet": ["/static/23a7237a553ebc88cda48d1db2ab9714/7fc1e/5_Banner_736.png 288w", "/static/23a7237a553ebc88cda48d1db2ab9714/a5df1/5_Banner_736.png 576w", "/static/23a7237a553ebc88cda48d1db2ab9714/3cbba/5_Banner_736.png 1152w", "/static/23a7237a553ebc88cda48d1db2ab9714/392b1/5_Banner_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Low-contrast banner notification</Caption>
      </Column>
    </Row>
    <h3>{`Notification panel`}</h3>
    <p>{`Notification panels are helpful for users who receive a large number of system-generated notifications or may need to reference their notifications later. They are used in conjunction with toasts to alert users of notifications as they come in.`}</p>
    <p>{`Notification panels ensure that users can access and read all notifications without cluttering the screen with many persistent notifications. They also provide a consistent experience to users who need more time to read notifications, use a screen reader, or prefer to limit the notifications they receive.`}</p>
    <p>{`More design iteration and user testing is needed before Carbon solidifies our guidance for notification panel and creates a notification panel component. Keep an eye out for refined usage examples in the future. If your team has developed a notification panel component, consider contributing it to Carbon so that it can be made available to others.`}</p>
    <h4>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Give users the ability to manage notification preferences.`}</li>
      <li parentName="ul">{`Don’t send the same notification multiple times if users don’t interact with it.`}</li>
      <li parentName="ul">{`List notifications in chronological order.`}</li>
      <li parentName="ul">{`Notifications can be grouped by source or urgency.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.53804347826086%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACmklEQVQ4y62TzU8aURTFZ+FOiw1FYRREYBgYU6hAgVSRNEar6+676KaJNmxdYCKNQsGCkhSb2Bg37v0v1PRDa2Ka2FSgoYi6IAim1oRYT+97tLTufcnN+5iZ35x7z7sCbngIvy4vsba2hkQ8joWFBSSTSczPzzcjkUhc27Pns7OzCIfDmJ6epggjMjOD5eW3ODs7g1Cv1+H3+3BLdRsmswRR1EGlUjWjvb29GWzf0tKCwNAQQqEQJiYmMPk8hCdPn8HjcaFYPGwAh4eHodOJsNlskK1WyLIMK80s7HZ7c202m9Ha2opMKoWrK6BGitjY2/6M+/4ASod/gMFgENrOTtx13INFVuhjGSaTCRaLBb29vRzE1hKFVqtFklIuFL7jazaH8kkRm3sncDxOoFTINoCBQAAajQbd3Xp0EFgURej1etp3w9jTQ+p1/IzB29raEKe6Hh+fELSA8/Mf2Hr3AR6vH4d/FQ4ODnLAwMAA3G4XnE4nJEniqh7QmdfrhcvlgqIoUKvVSKWSOK1Wkc3l8PPiAp+238Pjdv8DMpDVYsbYyAiGqOA+nw8WAiqyFSMPg9wEVhb2I7X6Dl4lZlAul5HPf0OlUsH2xw0yxfOfQkq5i1LqU/p48Zkyo9EIE6XbRwZJUsMUZhoz5WXsBY6OjpDNZlElpRubW9eBTBUr/Pj4OFfLUmTuMsCjsTGMjo7yc4fDwa/O4mIaldNT5PJ5VGs17O7u0tXzo1QqNYD9/f38RZtd4RAG7+jQQOwywK44SbGZ19RgMEAQBExNTWF//wt2dnZwcHCA9fV1ftWKxSKES+qUN0tLdPvnkFp8Td2QRJy6hsXcXBSxWAzRaJQHW0ciEaTTaayurmJlZYXPmUyGjEqhRmqFm+7l32nZjUdPahShAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Notification panel in a product",
            "title": "Notification panel in a product",
            "src": "/static/0fc22e702ba5e9d7921896e18af5c6e6/3cbba/6_Panel_736.png",
            "srcSet": ["/static/0fc22e702ba5e9d7921896e18af5c6e6/7fc1e/6_Panel_736.png 288w", "/static/0fc22e702ba5e9d7921896e18af5c6e6/a5df1/6_Panel_736.png 576w", "/static/0fc22e702ba5e9d7921896e18af5c6e6/3cbba/6_Panel_736.png 1152w", "/static/0fc22e702ba5e9d7921896e18af5c6e6/392b1/6_Panel_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Notification panel with the Gray 100 theme</Caption>
      </Column>
    </Row>
    <h3>{`Modal`}</h3>
    <p>{`Modals interrupt the user and pause their current task. They are highly disruptive to users and should be used sparingly. Only use a modal when the message is critical and needs the user’s immediate attention or action. Modals persist until users engage with them and only disappear when dismissed by the user.`}</p>
    <h4>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Only display one modal notification at a time.`}</li>
      <li parentName="ul">{`Use modals when you need to immediately interrupt a user’s task.`}</li>
      <li parentName="ul">{`Give users clear steps to resolve and dismiss the notification.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.53804347826086%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACcElEQVQ4y61TS2tTQRjN1oiQEmsS6CZ1pdZVyfsWQ2gWSruoSFtjQEFwpyZNo5EkN+/Hzb29aQKNpQR/R/Owad1Y8A8ILiJk1VWb0Bry4jgzEUqg4KYXDt83l+HM+eacUeCaP8VwOES5XEY0GkUikUA8Hp9ALBb7D+Lg+QgkaQudThuKwWAAt9sNg8EAi8UCk8kEs9nMeqvVytZXwWg0wWox4hFngs08j+Wlxzg5OYFiNBphb28P4XAYqVSKqUomk+z0bDYLQRBYnYQAeUvCB17G6408Xr6TEBNKuDjvjEculUoIBAIIBoMIhUKMXBRFyLKMfD6PQqEwxjbFNvufiEfw4k0Y9tUouKdhvP0oXRLu7u4i8CmIYIhHJMIzFa1WC6enp2i32+j1euj2+/jT72FELp72O0RELBJEOhFCJhklB8lkLyGkzlBl9+4/wLzBhIdzc1hYWECz2US322WEnU4H/bMzDAhwfkFqG1+Ikel0hk2SyWTZBHQvI/R4vbhx8xZu39FCNTWF6elprKys4Pn6OlYJXq2t4cfyM/x2LuGX4wl+OpfxmTibliSIQo4QZiYJfT4fNBoN7s7OQq/XM6jVanA2G/x+PzY8HsheH7bf+1HwbGJn0w8xnYaQyyGXu4LQSxSqVCrMzMxAq9VCp9NBqVTCbrejUNxBVpQhEHOyxBAKgUCk6si4Eqk0CcVi8ZKQ53nYbBwWF51wOByMiOM4uFwuFnYaoQRBnIafBp72NPT/gk8fBVXKCGkOj4+/o/71AAeNI3w7OsLh4SFDo9FApVJBvV5HrVZjPa3VanVca1XsV/bZmu6nJiqu+y3/BYdK4AXGSossAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Modal notification in a product",
            "title": "Modal notification in a product",
            "src": "/static/0da2a9b29c854d840b540b0058a47cff/3cbba/7_Modal_736.png",
            "srcSet": ["/static/0da2a9b29c854d840b540b0058a47cff/7fc1e/7_Modal_736.png 288w", "/static/0da2a9b29c854d840b540b0058a47cff/a5df1/7_Modal_736.png 576w", "/static/0da2a9b29c854d840b540b0058a47cff/3cbba/7_Modal_736.png 1152w", "/static/0da2a9b29c854d840b540b0058a47cff/392b1/7_Modal_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Danger modal notification</Caption>
      </Column>
    </Row>
    <h2>{`Considerations when designing`}</h2>
    <h3>{`Notification priority`}</h3>
    <p>{`Notifications range in their priority and therefore should vary in their disruptiveness to users. It is important to match the urgency and priority of the information being communicated to the visual style and behavior of the notification.`}</p>
    <p>{`Carbon offers two visual styles for inline and toast notifications. The high-contrast style is more visually disruptive and should be used for notifications that are urgent or critical. The low-contrast style is better for supplemental messaging or other low priority use cases. Toast and inline notifications can use different styles, but you should never mix styles within the variations.`}</p>
    <h3>{`User action`}</h3>
    <h4>{`Optional action`}</h4>
    <p>{`Notifications can include a ghost button that lets users interact with them. Taking action on these notifications should be optional and should not block the user from continuing with their current task. This action frequently takes users to a flow or page related to the message, where they can resolve the notification. All notifications should be dismissed when users interact with them.`}</p>
    <h4>{`Required action`}</h4>
    <p>{`Some notifications must block users from dismissing the notification or continuing with their task until the message has been addressed. This is common in forms when there are errors or empty input fields. Notifications that prevent users from continuing should be relevant to the current task and provide the user with the steps needed to resolve them or continue with their current task.`}</p>
    <p>{`Blocking users from continuing with a task is disruptive and hinders the overall experience, so these notifications should only be used when it is critical the user sees the notification or the user needs to take action right away.`}</p>
    <h3>{`Notification message`}</h3>
    <p>{`It’s important to consider the user’s context when writing notification messages. Use language that is accessible to the user and that will be easily understood. Use a tone that is appropriate for the situation and notification.`}</p>
    <p>{`Because task-generated notifications are sent in response to user action, it isn’t necessary to give users extensive background information. Conversely, system-generated notifications are generally not relevant to the user’s current task so you should ensure the user has sufficient context to understand the notification.`}</p>
    <DoDontRow mdxType="DoDontRow">
  <DoDont caption="Do use clear and concise language." text="Success! Your resource has been created." aspectRatio="1:1" mdxType="DoDont" />
  <DoDont type="dont" caption="Do not use technical jargon or language unfamiliar to the user." text="503 Service Unavailable" aspectRatio="1:1" mdxType="DoDont" />
    </DoDontRow>
    <DoDontRow mdxType="DoDontRow">
  <DoDont caption="Do ensure users konw how to take action if it is necessary." text="Script failed to run. Check the log for more detail." aspectRatio="1:1" mdxType="DoDont" />
  <DoDont tyoe="dont" caption="Do not leave users without next steps." text="Instance was not created." aspectRatio="1:1" mdxType="DoDont" />
    </DoDontRow>
    <h2>{`Accessibility`}</h2>
    <p><strong parentName="p">{`Don’t use notifications that dismiss on a timer for critical or emergency messages.`}</strong>{` Some users with disabilities need more time to read or interact with messages and timed toasts may not provide sufficient time.
`}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Understanding/no-timing.html"
      }}>{`WCAG 2.1 success criterion 2.2.4 (AAA)`}</a></p>
    <p><strong parentName="p">{`Users should be able to manage or limit noncritical notifications.`}</strong>{` This gives users the control to reduce the number of distractions or disruptions they receive, which is particularly helpful for users with cognitive limitations.
`}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Understanding/interruptions.html"
      }}>{`WCAG 2.1 success criterion 2.2.3 (AAA)`}</a></p>
    <h2>{`Related components and patterns`}</h2>
    <Row mdxType="Row">
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4>{`Components`}</h4>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/modal/usage"
            }}>{`Modal`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/notification/usage"
            }}>{`Notification`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/UI-shell-right-panel/usage"
            }}>{`UI shell`}</a></li>
        </ul>
      </Column>
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4>{`Patterns`}</h4>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.carbondesignsystem.com/patterns/dialog-pattern/"
            }}>{`Dialogs`}</a></li>
          <li parentName="ul">{`Error states (future)`}</li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/patterns/forms-pattern/"
            }}>{`Forms`}</a></li>
        </ul>
      </Column>
    </Row>
    <p>{`The Error state pattern is currently being planned. If you would like to contribute, please see our `}<a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/how-to-contribute/pattern"
      }}>{`guidelines for contributions`}</a>{`.`}</p>
    <h2>{`References`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://psnet.ahrq.gov/primer/alert-fatigue"
        }}>{`Alert Fatigue`}</a>{` (Patient Safety Network, 2019)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-1.1/#aria-live"
        }}>{`Aria Live Regions`}</a>{` (W3C, 2017)`}</li>
      <li parentName="ul">{`Duncan P. Brumby, Christian P. Janssen, and Gloria Mark, `}<a parentName="li" {...{
          "href": "https://link.springer.com/chapter/10.1007/978-1-4842-4221-6_9"
        }}>{`How Do Interruptions Affect Productivity?`}</a>{` (Rethinking Productivity in Software Engineering, 2019)`}</li>
      <li parentName="ul">{`Kim Flaherty, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/indicators-validations-notifications/"
        }}>{`Indicators, Validations, and Notifications`}</a>{` (Nielsen Norman Group, 2015)`}</li>
      <li parentName="ul">{`Aurora Harley, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/visibility-system-status/"
        }}>{`Visibility of System Status`}</a>{` (Nielsen Norman Group, 2018)`}</li>
      <li parentName="ul">{`Jakob Nielsen, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/ten-usability-heuristics/"
        }}>{`10 Usability Heuristics for User Interface Design`}</a>{` (Nielsen Norman Group, 1994)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/standards-guidelines/wcag/"
        }}>{`Web Content Accessibility Guidelines`}</a>{` (W3C, 2018)`}</li>
    </ul>
    <h2>{`Feedback`}</h2>
    <p>{`Help us improve this pattern by providing feedback, asking questions, and leaving any other comments on `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/issues/new?assignees=&labels=feedback&template=feedback.md"
      }}>{`GitHub`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      